import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Mail, Loader2, ArrowRight, CheckCircle2, AlertCircle, Check } from 'lucide-react';
import { supabase, isConfigured } from '../services/supabase';
import Footer from './Footer';
import { ProductSwitcher, MobileAppSwitcher } from './AppSwitcher';

const LoginPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState<'login' | 'signup'>(
    searchParams.get('mode') === 'signup' ? 'signup' : 'login'
  );
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedMarketing, setAcceptedMarketing] = useState(false);

  useEffect(() => {
    const refCode = searchParams.get('ref');
    if (refCode) {
      localStorage.setItem('referral_code', refCode);
      try {
        document.cookie = `rehub_referral_code=${encodeURIComponent(refCode)}; path=/; max-age=${60 * 60 * 24 * 14}; SameSite=Lax`;
      } catch {}
    }
  }, [searchParams]);

  const handleGoogleSignIn = async () => {
    if (!acceptedTerms) {
      setError('Please accept the Terms and Conditions to continue.');
      return;
    }

    if (!isConfigured) {
      setError('System Error: Supabase connection not configured.');
      return;
    }

    setGoogleLoading(true);
    setError(null);

    try {
      if (mode === 'signup' && acceptedMarketing) {
        localStorage.setItem('pending_marketing_consent', 'true');
      }

      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          },
        },
      });

      if (error) throw error;
    } catch (err: any) {
      console.error('Google Sign-In Error:', err);
      setError(err.message || 'Failed to sign in with Google');
      setGoogleLoading(false);
    }
  };

  const handleMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email) {
      setError('Please enter your email address');
      return;
    }

    if (!acceptedTerms) {
      setError('Please accept the Terms and Conditions to continue.');
      return;
    }

    if (website.trim()) {
      setError('Something went wrong. Please try again.');
      return;
    }

    if (!isConfigured) {
      setError('Authentication is not configured. Please set up Supabase.');
      return;
    }

    setLoading(true);

    try {
      const options: any = {
        emailRedirectTo: window.location.origin,
        data: { signup_app: 'hdrphoto' },
      };

      if (mode === 'signup') {
        localStorage.setItem('pending_marketing_consent', acceptedMarketing ? 'true' : 'false');
      }

      const { error } = await supabase.auth.signInWithOtp({
        email,
        options,
      });

      if (error) throw error;
      setSent(true);
    } catch (err: any) {
      console.error('Supabase Auth Error:', err);
      if (err.message === 'Failed to fetch' || !navigator.onLine) {
        setError('Connection failed. Please check your internet or firewall settings.');
      } else if (err.status === 400) {
        setError('Invalid email address or request.');
      } else {
        setError(err.message || 'Failed to send magic link');
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleMode = () => {
    setMode(mode === 'login' ? 'signup' : 'login');
    setError(null);
    setSent(false);
    setAcceptedTerms(false);
    setAcceptedMarketing(false);
    setWebsite('');
  };

  const LoginShell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div className="min-h-screen flex flex-col bg-slate-900 relative overflow-hidden">
      {/* Background decor */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-brand-900/20 rounded-full blur-[100px]"></div>
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-brand-800/15 rounded-full blur-[100px]"></div>
      </div>

      {/* Header */}
      <header className="sticky top-0 left-0 right-0 z-40 bg-black/60 backdrop-blur-md border-b border-slate-800/80 shrink-0">
        <div className="flex flex-col md:flex-row md:items-center md:h-16 px-4 sm:px-6">
          <div className="flex items-center justify-between w-full min-h-14 md:min-h-0 py-3 md:py-0 gap-3">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0">
              <a href="/" className="flex items-center shrink-0 group h-10 w-[110px] sm:w-[140px]">
                <img
                  src="/hdr-photo-logo.png"
                  alt="HDR Photo Studio"
                  className="h-10 w-auto max-w-full object-contain object-left group-hover:opacity-90 transition-all"
                />
              </a>
              <ProductSwitcher currentApp="hdrphoto" />
            </div>
          </div>
          {/* Row 2 mobile only: app switcher */}
          <div className="md:hidden border-t border-slate-800 py-2.5 flex items-center justify-center gap-3 overflow-x-auto shrink-0">
            <MobileAppSwitcher currentApp="hdrphoto" />
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 flex items-center justify-center p-6 relative z-10">
        {children}
      </main>

      <Footer />
    </div>
  );

  if (sent) {
    return (
      <LoginShell>
        <div className="max-w-md w-full bg-slate-900/90 backdrop-blur-xl border border-slate-800 rounded-3xl p-8 text-center shadow-2xl animate-fade-in">
          <div className="w-16 h-16 bg-emerald-900/30 text-emerald-400 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 size={32} />
          </div>
          <h2 className="text-2xl font-bold text-slate-100 mb-2">Check your email</h2>
          <p className="text-slate-400 mb-6">
            We've sent a magic link to <span className="text-slate-200 font-medium">{email}</span>.
            <br />Click it to {mode === 'signup' ? 'create your account' : 'log in'}.
          </p>
          <button
            onClick={() => setSent(false)}
            className="text-brand-400 hover:text-brand-300 text-sm font-medium transition-colors"
          >
            Try a different email
          </button>
        </div>
      </LoginShell>
    );
  }

  return (
    <LoginShell>
      <div className="max-w-md w-full bg-slate-900/90 backdrop-blur-xl border border-slate-800 rounded-3xl p-8 shadow-2xl animate-fade-in">
        <div className="text-center mb-6">
          <div className="flex justify-center mb-4">
            <img
              src="/hdr-photo-logo.png"
              alt="HDR Photo Studio"
              className="h-32 w-auto object-contain mb-6"
            />
          </div>
          <h1 className="text-3xl font-bold text-slate-100 mb-2">
            {mode === 'signup' ? 'Create Account' : 'Welcome Back'}
          </h1>
          <p className="text-slate-400">
            {mode === 'signup'
              ? 'Sign up to get 10 HDR Credits a month and start processing your photos!'
              : 'Sign in to access your dashboard.'}
          </p>
        </div>

        {/* Consents */}
        <div className="mb-6 space-y-3 text-left bg-slate-800/50 p-3 rounded-xl border border-slate-800">
          <label className="flex items-start gap-3 cursor-pointer group">
            <div className="relative flex items-center pt-0.5">
              <input
                type="checkbox"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                className="peer h-4 w-4 shrink-0 rounded border-slate-600 bg-slate-900 ring-offset-slate-900 focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 appearance-none border checked:bg-brand-600 checked:border-brand-600 transition-all"
              />
              <Check className="absolute top-0.5 left-0.5 h-3 w-3 text-white opacity-0 peer-checked:opacity-100 pointer-events-none transition-opacity" strokeWidth={3.5} />
            </div>
            <span className="text-xs text-slate-400 group-hover:text-slate-300 transition-colors select-none leading-relaxed">
              I accept the{' '}
              <a href="https://www.rehub.studio/terms-conditions" target="_blank" rel="noopener noreferrer" className="text-brand-400 hover:underline hover:text-brand-300 font-medium" onClick={(e) => e.stopPropagation()}>
                Terms and Conditions
              </a>{' '}
              and{' '}
              <a href="https://www.rehub.studio/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-brand-400 hover:underline hover:text-brand-300 font-medium" onClick={(e) => e.stopPropagation()}>
                Privacy Policy
              </a>.
            </span>
          </label>

          {mode === 'signup' && (
            <label className="flex items-start gap-3 cursor-pointer group animate-fade-in">
              <div className="relative flex items-center pt-0.5">
                <input
                  type="checkbox"
                  checked={acceptedMarketing}
                  onChange={(e) => setAcceptedMarketing(e.target.checked)}
                  className="peer h-4 w-4 shrink-0 rounded border-slate-600 bg-slate-900 ring-offset-slate-900 focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 appearance-none border checked:bg-brand-600 checked:border-brand-600 transition-all"
                />
                <Check className="absolute top-0.5 left-0.5 h-3 w-3 text-white opacity-0 peer-checked:opacity-100 pointer-events-none transition-opacity" strokeWidth={3.5} />
              </div>
              <span className="text-xs text-slate-400 group-hover:text-slate-300 transition-colors select-none leading-relaxed">
                I agree to receive marketing, offers, and updates from HDR Photo Studio.{' '}
                <span className="text-slate-500 text-[10px]">(Optional)</span>
              </span>
            </label>
          )}
        </div>

        {error && (
          <div className="flex items-start gap-2 p-3 bg-red-900/20 border border-red-900/50 rounded-lg text-red-400 text-sm mb-6 animate-fade-in">
            <AlertCircle size={16} className="shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleMagicLink} className="space-y-4">
          {/* Honeypot */}
          <div className="absolute -left-[9999px] top-0 h-0 w-0 overflow-hidden" aria-hidden="true">
            <label htmlFor="website">Website</label>
            <input
              type="text"
              id="website"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <label htmlFor="email" className="block text-sm font-medium text-slate-300">
              Email Address
            </label>
            <div className="relative group">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-500 group-focus-within:text-brand-400 transition-colors">
                <Mail size={18} />
              </div>
              <input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="block w-full pl-10 pr-3 py-3 bg-black border border-slate-700 rounded-xl text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-brand-500/50 focus:border-brand-500 transition-all"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading || googleLoading}
            className="w-full flex items-center justify-center gap-2 py-3.5 px-4 bg-brand-600 hover:bg-brand-500 text-white rounded-xl font-semibold transition-all shadow-lg hover:shadow-brand-900/30 disabled:opacity-50 disabled:cursor-not-allowed group"
          >
            {loading ? (
              <Loader2 size={20} className="animate-spin" />
            ) : (
              <>
                {mode === 'signup' ? 'Create Account' : 'Sign In'}
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </>
            )}
          </button>
        </form>

        {/* Divider */}
        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-slate-700"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-3 bg-slate-900 text-slate-500">or continue with</span>
          </div>
        </div>

        {/* Google Sign-In */}
        <button
          type="button"
          onClick={handleGoogleSignIn}
          disabled={loading || googleLoading}
          className="w-full flex items-center justify-center gap-3 py-3.5 px-4 bg-white hover:bg-gray-100 text-gray-800 rounded-xl font-semibold transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {googleLoading ? (
            <Loader2 size={20} className="animate-spin text-gray-600" />
          ) : (
            <>
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              Continue with Google
            </>
          )}
        </button>

        <div className="mt-6 pt-6 border-t border-slate-800 text-center">
          <p className="text-slate-400 text-sm">
            {mode === 'signup' ? 'Already have an account?' : "Don't have an account?"}
          </p>
          <button
            onClick={toggleMode}
            className="text-brand-400 hover:text-brand-300 font-medium text-sm mt-1 transition-colors hover:underline"
          >
            {mode === 'signup' ? 'Log in here' : 'Sign up for free'}
          </button>
        </div>
      </div>
    </LoginShell>
  );
};

export default LoginPage;
